import { useState, useEffect } from 'react';
import { useAuthState } from '../hooks/useAuthState';
import { collection, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import { resolveInviteToken } from '../lib/invites';
import { getGroup } from '../lib/firestore/groups';
import { getEvent } from '../lib/firestore/events';
import { addGroupMember } from '../lib/firestore/groups';
import { createRSVP } from '../lib/firestore/events';
import '../styles/SponsorInvitesInbox.css';

const SponsorInvitesInbox = () => {
  const { user } = useAuthState();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadSponsorInvites();
  }, [user]);

  const loadSponsorInvites = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', user.uid),
        where('type', 'in', ['group_sponsor', 'event_invite'])
      );

      const snapshot = await getDocs(q);
      const notifs = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const data = doc.data();
          let groupInfo = null;
          let eventInfo = null;

          if (data.type === 'group_sponsor' && data.groupId) {
            groupInfo = await getGroup(data.groupId);
          } else if (data.type === 'event_invite' && data.eventId) {
            eventInfo = await getEvent(data.eventId);
          }

          // Get sponsor info - try fromUserId first, then extract from message
          let sponsorName = 'Someone';
          if (data.fromUserId) {
            const sponsorDoc = await getDoc(doc(db, 'users', data.fromUserId));
            if (sponsorDoc.exists()) {
              const sponsorData = sponsorDoc.data();
              sponsorName = `${sponsorData.firstName || ''} ${sponsorData.lastName || ''}`.trim() || 'Someone';
            }
          } else if (data.message) {
            // Extract sponsor name from message (format: "Name sponsored you to join...")
            const match = data.message.match(/^([^ ]+ [^ ]+|[^ ]+) sponsored you/);
            if (match) {
              sponsorName = match[1];
            }
          }

          return {
            id: doc.id,
            ...data,
            groupInfo,
            eventInfo,
            sponsorName,
          };
        })
      );

      // Sort by created date, newest first
      notifs.sort((a, b) => {
        const aTime = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const bTime = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return bTime - aTime;
      });

      setNotifications(notifs);
    } catch (error) {
      console.error('Error loading sponsor invites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (notification) => {
    if (!user || processing[notification.id]) return;
    setProcessing({ ...processing, [notification.id]: true });

    try {
      if (!notification.inviteToken) {
        alert('Invalid invite token');
        return;
      }

      // Resolve the invite token
      const invite = await resolveInviteToken(notification.inviteToken);
      if (!invite) {
        alert('Invite has expired or is invalid');
        await markAsRead(notification.id);
        loadSponsorInvites();
        return;
      }

      if (invite.target.kind === 'group_join') {
        // Prepare member data
        const memberData = {
          userId: user.uid,
          role: invite.metadata?.role || 'member',
          joinedAt: new Date(),
        };
        
        // Only add sponsoredBy if it's a valid string
        const sponsorId = invite.metadata?.sponsorId || invite.issuedBy;
        if (sponsorId && typeof sponsorId === 'string') {
          memberData.sponsoredBy = sponsorId;
        }
        
        // Add user to group
        await addGroupMember(invite.target.id, memberData);

        // Mark notification as read
        await markAsRead(notification.id);

        // Navigate to group
        navigate(`/groups/${invite.target.id}?joined=true`);
      } else if (invite.target.kind === 'event_join') {
        // RSVP to event
        await createRSVP({
          userId: user.uid,
          eventId: invite.target.id,
          state: invite.metadata?.role === 'guest' ? 'interested' : 'going',
          plusOnes: 0,
        });

        // Mark notification as read
        await markAsRead(notification.id);

        // Navigate to event
        navigate(`/events/${invite.target.id}?joined=true`);
      }
    } catch (error) {
      console.error('Error accepting invite:', error);
      alert('Failed to accept invite. Please try again.');
    } finally {
      setProcessing({ ...processing, [notification.id]: false });
    }
  };

  const handleDecline = async (notification) => {
    if (!user || processing[notification.id]) return;
    setProcessing({ ...processing, [notification.id]: true });

    try {
      await markAsRead(notification.id);
      loadSponsorInvites();
    } catch (error) {
      console.error('Error declining invite:', error);
    } finally {
      setProcessing({ ...processing, [notification.id]: false });
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        status: 'read',
        readAt: new Date(),
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  if (loading) {
    return (
      <div className="sponsor-invites-inbox">
        <div className="loading">Loading invites...</div>
      </div>
    );
  }

  return (
    <div className="sponsor-invites-inbox">
      <header className="inbox-header">
        <h1>Sponsor Invites</h1>
        <button className="btn-icon" onClick={() => navigate('/home')}>
          ← Back
        </button>
      </header>

      <div className="inbox-content">
        {notifications.length === 0 ? (
          <div className="empty-inbox">
            <p>No sponsor invites</p>
            <small>When someone sponsors you to join a group or invites you to an event, it will appear here.</small>
          </div>
        ) : (
          <div className="invites-list">
            {notifications.map((notification) => (
              <div key={notification.id} className="sponsor-invite-card">
                <div className="invite-header">
                  <div className="invite-icon">
                    {notification.type === 'group_sponsor' ? '👥' : '📅'}
                  </div>
                  <div className="invite-title">
                    <h3>{notification.title}</h3>
                    <p className="invite-message">{notification.message}</p>
                  </div>
                </div>

                {notification.type === 'group_sponsor' && notification.groupInfo && (
                  <div className="group-preview">
                    <div className="group-preview-header">
                      <h4>{notification.groupInfo.name}</h4>
                      {notification.groupInfo.description && (
                        <p className="group-description">{notification.groupInfo.description}</p>
                      )}
                    </div>
                    <div className="group-preview-blur">
                      <div className="blur-overlay">
                        <p className="blur-message">
                          To see the group and members you must be part of the group
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {notification.type === 'event_invite' && notification.eventInfo && (
                  <div className="event-preview">
                    <h4>{notification.eventInfo.title}</h4>
                    {notification.eventInfo.description && (
                      <p className="event-description">{notification.eventInfo.description}</p>
                    )}
                    <button
                      className="btn-link"
                      onClick={() => navigate(`/events/${notification.eventId}`)}
                    >
                      View Event →
                    </button>
                  </div>
                )}

                <div className="invite-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleDecline(notification)}
                    disabled={processing[notification.id]}
                  >
                    {processing[notification.id] ? 'Processing...' : 'Decline'}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleAccept(notification)}
                    disabled={processing[notification.id]}
                  >
                    {processing[notification.id] ? 'Processing...' : 'Accept'}
                  </button>
                </div>

                {notification.createdAt?.toDate && (
                  <div className="invite-time">
                    {new Date(notification.createdAt.toDate()).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SponsorInvitesInbox;

